import sentryFetch from '@amzn/sentry-fetch';
import {GetReviewRequest} from "src/app/api/model/request/GetReviewRequest";
import {UpdateReviewRequest} from "src/app/api/model/request/UpdateReviewRequest";
import {ListReviewRequest} from "src/app/api/model/request/ListReviewRequest";
import {ExportReviewRequest} from "src/app/api/model/request/ExportReviewRequest";
import {ExportReviewResponse} from "src/app/api/model/response/ExportReviewResponse";
import {ListReviewResponse} from "src/app/api/model/response/ListReviewResponse";
import {UpdateReviewResponse} from "src/app/api/model/response/UpdateReviewResponse";
import {GetReviewResponse} from "src/app/api/model/response/GetReviewResponse";
import {isDev} from "src/app/constant/Constants";
import {CASE_TYPE_OVERRIDE_MAPPING} from "src/app/config/CaseTypeMapping";
import {
    MOCK_EXPORT_REVIEW_SUCCESS_RESPONSE,
    MOCK_GET_REVIEW_SUCCESS_RESPONSE,
    MOCK_GET_ODQ_REVIEW_SUCCESS_RESPONSE,
    MOCK_GET_RELIABILITY_REVIEW_SUCCESS_RESPONSE,
    MOCK_LIST_REVIEW_SUCCESS_RESPONSE,
    MOCK_UPDATE_REVIEW_SUCCESS_RESPONSE
} from "src/app/mock/MockApiResponse";
import UserInfoStore from "src/app/store/UserInfoStore";
import {logger} from "src/app/util/Logger";
import { recordAPICall, recordAPIError, recordAPISuccess } from "../util/Metrics";
import {REGION, STAGE} from "src/app/config/EnvConfig";
import {convertAwsRegion, EEP_API_URL_MAP} from "src/app/config/ApiConfig";

/**
 * Returns the API base URL for the current stage and region.
 * Uses the centralized EEP_API_URL_MAP from ApiConfig.
 */
const getApiEndpoint = (): string => {
    const stage = (STAGE as string).toLowerCase();
    const region = convertAwsRegion(REGION as string);
    return EEP_API_URL_MAP[stage]?.[region]?.endpoint ?? '';
};

const API_BASE = getApiEndpoint();

/**
 * Core fetch wrapper using sentryFetch for ALI Midway CORS authentication.
 * sentryFetch with followMidwayStepUp:true handles the full Midway login redirect
 * automatically when no valid session exists — no custom redirect logic needed.
 */
const apiFetch = async (method: string, path: string, body?: any): Promise<any> => {
    const url = `${API_BASE}${path}`;
    const response = await sentryFetch(url, {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
        sentryOptions: { followMidwayStepUp: true }
    } as any);
    if (!response.ok) {
        throw new Error(`API ${method} ${path} failed: ${response.status}`);
    }
    return response.json();
};

const OPERATION = {
    GET_REVIEW: 'GetReview',
    UPDATE_REVIEW: 'UpdateReview',
    LIST_REVIEW: 'ListReview',
    EXPORT_REVIEW: 'ExportReview'
} as const;

export namespace EEPApi {
    /**
     * Logs API request information
     */
    const logRequest = (operation: string, request: any) => {
        try {
            logger.info(`[${operation}] Request initiated`, {
                operation,
                userId: UserInfoStore.value,
                requestData: request,
                timestamp: new Date().toISOString()
            });
            recordAPICall(operation);
        } catch (error) {
            console.error('Logging error:', error);
        }
    };

    /**
     * Logs API response information
     */
    const logResponse = (operation: string, duration: number) => {
        try {
            logger.info(`[${operation}] Response received`, {
                operation,
                userId: UserInfoStore.value,
                duration,
                timestamp: new Date().toISOString()
            });
            recordAPISuccess(operation, duration);
        } catch (error) {
            console.error('Logging error:', error);
        }
    };

    /**
     * Logs API errors with full context
     */
    const logError = (operation: string, error: any, request?: any) => {
        try {
            logger.error(`[${operation}] Error occurred`, {
                operation,
                error: error.message || error,
                errorStack: error.stack,
                userId: UserInfoStore.value,
                requestData: request,
                timestamp: new Date().toISOString()
            });
            recordAPIError(operation);
        } catch (error) {
            console.error('Logging error:', error);
        }
    };

    /**
     * Transforms the case type and subtype of a request based on predefined mappings
     * @param request - The request object containing caseType and caseSubType
     * @throws {Error} If request object is null or undefined
     */
    const transformRequest = (request: any) => {
        if (!request) {
            throw new Error('Request object is required');
        }

        if (!('caseType' in request && 'caseSubType' in request)) {
            return;
        }

        if (request.caseType in CASE_TYPE_OVERRIDE_MAPPING &&
            request.caseSubType in CASE_TYPE_OVERRIDE_MAPPING[request.caseType]) {
            const originalCaseType = request.caseType;
            request.caseType = CASE_TYPE_OVERRIDE_MAPPING[originalCaseType][request.caseSubType].caseType;
            request.caseSubType = CASE_TYPE_OVERRIDE_MAPPING[originalCaseType][request.caseSubType].caseSubType;
        }
    };

    //exporting transformRequest in test env for unit testing purpose
    export const _transformRequest = process.env.NODE_ENV === 'test' ? transformRequest : undefined;


    export const getReview = async (request: GetReviewRequest): Promise<GetReviewResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.GET_REVIEW, request);

            if (isDev()) {
                if (request.reviewId.includes("reliability")) return MOCK_GET_RELIABILITY_REVIEW_SUCCESS_RESPONSE;
                if (request.reviewId.includes("odq")) return MOCK_GET_ODQ_REVIEW_SUCCESS_RESPONSE;
                return MOCK_GET_REVIEW_SUCCESS_RESPONSE;
            }
            const path = `/reviews/` + request.reviewId;
            const response = await apiFetch('GET', path);
            logResponse(OPERATION.GET_REVIEW, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.GET_REVIEW, error, request);
            throw error;
        }
    };


    export const updateReview = async (request: UpdateReviewRequest): Promise<UpdateReviewResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.UPDATE_REVIEW, request);

            if (isDev()) {
                return MOCK_UPDATE_REVIEW_SUCCESS_RESPONSE;
            }
            const path = `/reviews/` + request.reviewId;
            const response = await apiFetch('PUT', path, request);
            logResponse(OPERATION.UPDATE_REVIEW, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.UPDATE_REVIEW, error, request);
            throw error;
        }
    };

    export const listReview = async (request: ListReviewRequest): Promise<ListReviewResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.LIST_REVIEW, request);

            if (isDev()) {
                return MOCK_LIST_REVIEW_SUCCESS_RESPONSE;
            }
            const path = `/reviews/list`;
            transformRequest(request);
            const response = await apiFetch('POST', path, request);
            logResponse(OPERATION.LIST_REVIEW, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.LIST_REVIEW, error, request);
            throw error;
        }
    };

    export const exportReview = async (request: ExportReviewRequest): Promise<ExportReviewResponse> => {
        const startTime = Date.now();

        try {
            logRequest(OPERATION.EXPORT_REVIEW, request);

            if (isDev()) {
                return MOCK_EXPORT_REVIEW_SUCCESS_RESPONSE;
            }
            const path = `/reviews/download`;
            transformRequest(request);
            const response = await apiFetch('POST', path, request);
            logResponse(OPERATION.EXPORT_REVIEW, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.EXPORT_REVIEW, error, request);
            throw error;
        }
    };

    /**
     * Returns the authenticated user's alias from ALI principalId.
     * Called once on app load to display user identity in the nav bar.
     */
    export const whoAmI = async (): Promise<string> => {
        try {
            const response = await apiFetch('GET', '/whoami');
            return response.alias || '';
        } catch (error) {
            logger.error('[WhoAmI] Failed to fetch user alias', { error });
            return '';
        }
    };

}
